/**
 * A stable per-dialog idempotency key for scheduled-message CREATES.
 *
 * The scheduler dialog stamps ONE key at dialog-OPEN (in the AddScheduledMessageButtonReducer
 * edit* actions) and reuses it on any re-submit within the same open dialog. Stamping at open
 * rather than lazily on the first save makes the key synchronously stable, so the two clicks of a
 * fast double-click read the SAME key (see BE#372/#231). The backend (`user-messages`
 * before.create / error.create dedupe hooks + the `clientIdempotencyKey_1_unique_sparse` index)
 * collapses a re-submit onto the first-created doc, so a double-click / no-feedback re-save can no
 * longer mint a second recurring root. A fresh key is minted on every open (including when EDITING
 * an existing message, so a stored key is never reused — which would make the backend return the
 * old doc instead of creating) and the draft is cleared on close, so each open session is independent.
 */

export const CLIENT_IDEMPOTENCY_KEY_PREFIX = 'ui_';

/** Generate a fresh key. Uses crypto.randomUUID with a defensive fallback (guarded like stable-instance-id). */
export function newClientIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return CLIENT_IDEMPOTENCY_KEY_PREFIX + crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID.
  return `${CLIENT_IDEMPOTENCY_KEY_PREFIX}${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Returns the existing session key if the dialog already has one, else a fresh key.
 * Pure given the generator (injectable for tests). This is the single decision point that keeps
 * a key STABLE across re-submits of the same dialog.
 */
export function ensureClientIdempotencyKey(
  current: string | undefined | null,
  gen: () => string = newClientIdempotencyKey,
): string {
  return current && current.length > 0 ? current : gen();
}
